import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Video,
  PlayCircle,
  Play,
  Link2,
  AlertCircle,
  Loader2,
  Layers,
  ClipboardCheck,
  Grid3x3,
  Gamepad2,
  BookOpen,
  Pencil,
  Copy,
} from 'lucide-react'
import { toast } from 'sonner'
import { contentInspectionApi } from '@/api/contentInspection'
import { BuildupEditor } from '@/components/BuildupEditor'
import { EssentialCheckEditor } from '@/components/EssentialCheckEditor'
import {
  getInspectionGameQueryKey,
  type InspectionLessonPath,
} from '@/pages/contentInspectionTreeState'
import { VideoPlayerModal } from './VideoPlayerModal'
import { ToonPreviewModal } from './ToonPreviewModal'
import { WarmupTextView } from './WarmupTextView'
import { BuildupDetailModal } from './BuildupDetailModal'
import { EssentialCheckDetailModal } from './EssentialCheckDetailModal'
import { QueboardPreviewModal } from './QueboardPreviewModal'
import { GamesPreviewModal } from './GamesPreviewModal'
import { PauseAndGoExperienceModal } from './PauseAndGoExperienceModal'

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

function formatDuration(ms: number | null | undefined): string {
  if (!ms) return '-'
  const totalSeconds = Math.floor(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function VideoPanel({
  lessonPath,
  qid,
}: {
  lessonPath: InspectionLessonPath
  qid: string
}) {
  const { lessonId } = lessonPath
  const [modal, setModal] = useState<
    | { kind: 'main' }
    | { kind: 'summary' }
    | { kind: 'intro' }
    | { kind: 'outro' }
    | { kind: 'buildup' }
    | { kind: 'editBuildup' }
    | { kind: 'essentialCheck' }
    | { kind: 'editEssentialCheck' }
    | { kind: 'queboard' }
    | { kind: 'games' }
    | { kind: 'pgExperience'; initialId: number | null }
    | null
  >(null)

  const { data: info, isLoading: infoLoading } = useQuery({
    queryKey: ['inspection', 'quetureInfo', lessonId, qid],
    queryFn: () => contentInspectionApi.getQuetureInfo(lessonId, qid, true),
    staleTime: 5 * 60 * 1000,
  })

  const { data: content, isLoading: contentLoading } = useQuery({
    queryKey: ['inspection', 'quetureContent', qid],
    queryFn: () => contentInspectionApi.getQuetureContent(qid),
    staleTime: 5 * 60 * 1000,
  })

  const gamesQuery = useQuery({
    queryKey: getInspectionGameQueryKey(lessonPath),
    queryFn: () => contentInspectionApi.getGamesByLessonPath(
      lessonPath.curriculumId,
      lessonPath.courseId,
      lessonPath.moduleId,
      lessonPath.lessonId
    ),
    staleTime: 5 * 60 * 1000,
  })

  if (infoLoading || contentLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  const pauseAndGos = info?.pauseAndGos ?? []
  const publishedCount = pauseAndGos.filter(p => p.published).length
  const games = gamesQuery.data ?? []
  const publishedGameCount = games.filter(game => game.published).length
  const draftGameCount = games.length - publishedGameCount

  const handleCopyQuetureId = async () => {
    try {
      await navigator.clipboard.writeText(qid)
      toast.success('깨처 ID를 복사했습니다.')
    } catch {
      toast.error('깨처 ID를 복사하지 못했습니다.')
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* 영상 정보 */}
      <section>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-700">영상 정보</h3>
          <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
            <span className="text-xs font-medium text-slate-500">깨처 ID</span>
            <code className="text-xs text-slate-700">{qid}</code>
            <button
              type="button"
              onClick={handleCopyQuetureId}
              className="rounded p-0.5 text-slate-400 transition-colors hover:bg-white hover:text-slate-700"
              title="깨처 ID 복사"
              aria-label="깨처 ID 복사"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* 메인깨처 */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <Video className="w-4 h-4 text-blue-500" />
              메인깨처
            </div>
            {info?.hlsUrl ? (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs text-green-600">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  영상 있음
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setModal({ kind: 'main' })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
                  >
                    <Play className="w-3 h-3" />
                    재생
                  </button>
                  <a
                    href={info.hlsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex gap-1 items-center text-xs text-blue-500 hover:underline"
                  >
                    <Link2 className="w-3 h-3" />
                    HLS URL
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-red-500">
                <AlertCircle className="h-3.5 w-3.5" />
                영상 없음
              </div>
            )}
          </div>

          {/* 반짝깨처 */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <Video className="w-4 h-4 text-purple-500" />
              반짝깨처
            </div>
            {info?.summaryHlsUrl ? (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs text-green-600">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  영상 있음
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setModal({ kind: 'summary' })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-purple-500 rounded hover:bg-purple-600"
                  >
                    <Play className="w-3 h-3" />
                    재생
                  </button>
                  <a
                    href={info.summaryHlsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex gap-1 items-center text-xs text-blue-500 hover:underline"
                  >
                    <Link2 className="w-3 h-3" />
                    HLS URL
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-slate-400">없음</div>
            )}
          </div>

          {/* Pause & Go */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <PlayCircle className="w-4 h-4 text-amber-500" />
              Pause &amp; Go
            </div>
            <div className="space-y-1 text-xs">
              <div className="text-slate-700">총 {pauseAndGos.length}개</div>
              {publishedCount > 0 && <div className="text-green-600">게시됨: {publishedCount}개</div>}
            </div>
            {pauseAndGos.length > 0 && (
              <button
                onClick={() => setModal({ kind: 'pgExperience', initialId: null })}
                className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-amber-500 rounded hover:bg-amber-600"
              >
                체험
              </button>
            )}
          </div>

          {/* 빌드업 */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <Layers className="w-4 h-4 text-indigo-500" />
              빌드업
            </div>
            {content?.buildup?.length ? (
              <div className="space-y-2">
                <div className="text-xs text-slate-700">
                  {content.buildup.length}스텝 · {content.buildup.reduce((s, st) => s + st.items.length, 0)}아이템
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setModal({ kind: 'buildup' })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-indigo-500 rounded hover:bg-indigo-600"
                  >
                    상세 보기
                  </button>
                  <button
                    onClick={() => setModal({ kind: 'editBuildup' })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
                  >
                    <Pencil className="w-3 h-3" />
                    수정
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-slate-400">없음</div>
                <button
                  onClick={() => setModal({ kind: 'editBuildup' })}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
                >
                  <Pencil className="w-3 h-3" />
                  설정
                </button>
              </div>
            )}
          </div>

          {/* 필수 체크 */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <ClipboardCheck className="w-4 h-4 text-teal-500" />
              필수 체크
            </div>
            {content?.essentialCheck?.length ? (
              <div className="space-y-2">
                <div className="text-xs text-slate-700">{content.essentialCheck.length}개 항목</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setModal({ kind: 'essentialCheck' })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-teal-500 rounded hover:bg-teal-600"
                  >
                    상세 보기
                  </button>
                  <button
                    onClick={() => setModal({ kind: 'editEssentialCheck' })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded hover:bg-teal-100"
                  >
                    <Pencil className="w-3 h-3" />
                    수정
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-slate-400">없음</div>
                <button
                  onClick={() => setModal({ kind: 'editEssentialCheck' })}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded hover:bg-teal-100"
                >
                  <Pencil className="w-3 h-3" />
                  설정
                </button>
              </div>
            )}
          </div>

          {/* 깨보드 */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <Grid3x3 className="w-4 h-4 text-fuchsia-500" />
              깨보드
            </div>
            {content?.queboardConfig?.length ? (
              <div className="space-y-2">
                <div className="text-xs text-slate-700">{content.queboardConfig.length}개 수식 설정</div>
                <button
                  onClick={() => setModal({ kind: 'queboard' })}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-fuchsia-500 rounded hover:bg-fuchsia-600"
                >
                  미리보기
                </button>
              </div>
            ) : (
              <div className="text-xs text-slate-400">없음</div>
            )}
          </div>

          {/* 게임 */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <Gamepad2 className="w-4 h-4 text-orange-500" />
              게임
            </div>
            {gamesQuery.isLoading ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="size-3.5 animate-spin" />
                경로 연결 조회 중
              </div>
            ) : gamesQuery.error ? (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs text-red-500">
                  <AlertCircle className="size-3.5" />
                  게임 연결을 불러오지 못했습니다.
                </div>
                <button
                  type="button"
                  onClick={() => gamesQuery.refetch()}
                  className="text-xs font-medium text-red-600 hover:underline"
                >
                  다시 시도
                </button>
              </div>
            ) : games.length > 0 ? (
              <div className="space-y-2">
                <div className="text-xs text-slate-700">
                  총 {games.length}개
                  <span className="ml-1.5 text-emerald-600">공개 {publishedGameCount}</span>
                  {draftGameCount > 0 && (
                    <span className="ml-1.5 text-amber-600">Draft {draftGameCount}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setModal({ kind: 'games' })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-orange-500 rounded hover:bg-orange-600"
                  >
                    미리보기
                  </button>
                  <Link
                    to="/contents/games"
                    className="text-xs font-medium text-orange-700 hover:underline"
                  >
                    연결 관리
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-slate-400">이 경로에 연결된 게임 없음</div>
                <Link
                  to="/contents/games"
                  className="text-xs font-medium text-orange-700 hover:underline"
                >
                  게임 연결 관리
                </Link>
              </div>
            )}
          </div>

          {/* 깨봉툰 인트로 */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <BookOpen className="w-4 h-4 text-blue-500" />
              깨봉툰 인트로
            </div>
            {content?.qnIntro?.length ? (
              <div className="space-y-2">
                <div className="text-xs text-slate-700">{content.qnIntro.length}장</div>
                <button
                  onClick={() => setModal({ kind: 'intro' })}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
                >
                  <Play className="w-3 h-3" />
                  체험
                </button>
              </div>
            ) : (
              <div className="text-xs text-slate-400">없음</div>
            )}
          </div>

          {/* 깨봉툰 아웃트로 */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex gap-2 items-center mb-2 text-sm font-medium text-slate-700">
              <BookOpen className="w-4 h-4 text-rose-500" />
              깨봉툰 아웃트로
            </div>
            {content?.qnOutro?.length ? (
              <div className="space-y-2">
                <div className="text-xs text-slate-700">{content.qnOutro.length}장</div>
                <button
                  onClick={() => setModal({ kind: 'outro' })}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-rose-500 rounded hover:bg-rose-600"
                >
                  <Play className="w-3 h-3" />
                  체험
                </button>
              </div>
            ) : (
              <div className="text-xs text-slate-400">없음</div>
            )}
          </div>
        </div>
      </section>

      {/* Warmup Text / Short Point */}
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-700">텍스트 콘텐츠</h3>
        <div className="space-y-4">
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="mb-2 text-xs font-semibold tracking-wide text-blue-600 uppercase">핵심 포인트</div>
            <WarmupTextView text={info?.queturePoint} />
          </div>
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="mb-2 text-xs font-semibold tracking-wide text-amber-600 uppercase">생각열기</div>
            <WarmupTextView text={content?.warmupText} />
          </div>
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="mb-2 text-xs font-semibold tracking-wide text-emerald-600 uppercase">한줄 포인트</div>
            <WarmupTextView text={content?.shortPoint} stripPointPrefix />
          </div>
        </div>
      </section>

      {/* Pause & Go 상세 — 일시 숨김 (체험 모달로 검수)
      {pauseAndGos.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Pause &amp; Go 상세</h3>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="text-xs font-medium tracking-wide text-left uppercase bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">#</th>
                  <th className="px-4 py-2.5">시간</th>
                  <th className="px-4 py-2.5">타입</th>
                  <th className="px-4 py-2.5">상태</th>
                  <th className="px-4 py-2.5">퀴즈 ID</th>
                  <th className="px-4 py-2.5">블록퀴즈</th>
                  <th className="px-4 py-2.5">메시지</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {pauseAndGos.map((p, i) => {
                  const clickable = p.isBlockQuiz && !!p.quizId
                  return (
                    <tr
                      key={p.id}
                      onClick={clickable ? () => setModal({ kind: 'pgExperience', initialId: p.id }) : undefined}
                      className={cn(
                        'hover:bg-slate-50',
                        clickable && 'cursor-pointer'
                      )}
                    >
                    <td className="px-4 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-4 py-2 font-mono text-xs">{formatDuration(p.time)}</td>
                    <td className="px-4 py-2">
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                        {p.type}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                        p.published ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                      )}>
                        <div className={cn('h-1.5 w-1.5 rounded-full', p.published ? 'bg-green-500' : 'bg-amber-500')} />
                        {p.published ? '게시' : '미게시'}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{p.quizId || '-'}</td>
                    <td className="px-4 py-2">
                      {p.isBlockQuiz && (
                        <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">Y</span>
                      )}
                    </td>
                    <td className="max-w-[200px] truncate px-4 py-2 text-xs text-slate-500" title={p.msg ?? ''}>
                      {p.msg || '-'}
                    </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
      */}

      <VideoPlayerModal
        open={modal?.kind === 'main'}
        onClose={() => setModal(null)}
        title="메인깨처 재생"
        hlsUrl={info?.hlsUrl ?? null}
      />
      <VideoPlayerModal
        open={modal?.kind === 'summary'}
        onClose={() => setModal(null)}
        title="반짝깨처 재생"
        hlsUrl={info?.summaryHlsUrl ?? null}
      />
      {modal?.kind === 'intro' && (
        <ToonPreviewModal
          open
          onClose={() => setModal(null)}
          mode="intro"
          cards={(content?.qnIntro ?? []) as any}
        />
      )}
      {modal?.kind === 'outro' && (
        <ToonPreviewModal
          open
          onClose={() => setModal(null)}
          mode="outro"
          cards={(content?.qnOutro ?? []) as any}
        />
      )}
      <BuildupDetailModal
        open={modal?.kind === 'buildup'}
        onClose={() => setModal(null)}
        onEdit={() => setModal({ kind: 'editBuildup' })}
        buildup={content?.buildup ?? null}
      />
      <EssentialCheckDetailModal
        open={modal?.kind === 'essentialCheck'}
        onClose={() => setModal(null)}
        onEdit={() => setModal({ kind: 'editEssentialCheck' })}
        items={content?.essentialCheck ?? null}
      />
      {modal?.kind === 'editBuildup' && (
        <BuildupEditor
          quetureId={qid}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'editEssentialCheck' && (
        <EssentialCheckEditor
          quetureId={qid}
          onClose={() => setModal(null)}
        />
      )}
      <QueboardPreviewModal
        open={modal?.kind === 'queboard'}
        onClose={() => setModal(null)}
        entries={content?.queboardConfig ?? null}
      />
      <GamesPreviewModal
        open={modal?.kind === 'games'}
        onClose={() => setModal(null)}
        games={games}
      />
      <PauseAndGoExperienceModal
        open={modal?.kind === 'pgExperience'}
        onClose={() => setModal(null)}
        hlsUrl={info?.hlsUrl ?? null}
        pauseAndGos={pauseAndGos}
        initialSelectedId={modal?.kind === 'pgExperience' ? modal.initialId : null}
      />
    </div>
  )
}
